import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Select, message } from 'antd';
import Breadcrumbs from '../../components/Breadcrumbs';
import {
  PlusIcon,
  TrashIcon,
  InfoCircleIcon,
  DragHandleIcon,
  EscalateArrowIcon,
  CaretIcon,
  CheckmarkIcon,
} from '../../assets/icons';
import AddDesignationModal, {
  LEVEL_TONES,
  type NewDesignation,
} from './createDepartment/AddDesignationModal';

/* ------------------------------------------------------------------ *
 * Create Government Department (Build Hierarchy → Dept. Configuration)
 * Figma 1673-1209. Register a department + configure its designation ranks.
 * ------------------------------------------------------------------ */

interface Designation extends NewDesignation {
  id: string;
}

/** Territory hierarchies a department can be mapped to (demo list). */
const TERRITORY_HIERARCHIES = [
  'State Revenue (General Administration)',
  'State Police',
  'Panchayati Raj',
  'Urban Local Bodies',
];

/** Seed rows matching the design (editable in-place; demo data, no API yet). */
const SEED_DESIGNATIONS: Designation[] = [
  { id: 'd1', name: 'Principal Secretary (Revenue)', rank: 1, level: 'State Level', tone: 'state', terminal: false },
  { id: 'd2', name: 'District Collector / DM', rank: 2, level: 'District Level', tone: 'district', terminal: false },
  { id: 'd3', name: 'Sub-Divisional Officer (SDO)', rank: 3, level: 'Sub-division Level', tone: 'subdivision', terminal: false },
  { id: 'd4', name: 'Tahsildar / Circle Officer', rank: 4, level: 'Tahsil Level', tone: 'tahsil', terminal: true },
];

/** Core post name for escalation labels — drops the "(…)" / "/ …" qualifiers. */
const baseName = (name: string) => name.split('(')[0].split('/')[0].trim();

/** Control-mode options for the Department Identity card (expandable selector). */
const CONTROL_MODES = [
  {
    key: 'direct',
    title: 'Direct Control',
    desc: 'Managed directly by a designated reporting authority.',
  },
  {
    key: 'dual',
    title: 'Dual Control',
    desc: 'Governed or supervised by two designated authorities.',
  },
  {
    key: 'autonomous',
    title: 'Autonomous Control',
    desc: 'Operates independently within its authorized powers and governance framework.',
  },
] as const;

type ControlMode = (typeof CONTROL_MODES)[number]['key'];

/** Shared grid template so the header and body rows stay aligned. */
const GRID = 'grid grid-cols-[80px_minmax(180px,1fr)_160px_150px_minmax(190px,1fr)_92px] items-center';

/** Design-matched field styling (slate-300 border, rounded, focus ring). */
const INPUT =
  'w-full rounded-lg border border-[#cbd5e1] bg-white px-[15px] py-[11px] text-sm text-ink placeholder:text-[#94a3b8] transition-colors focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/15';

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <label className="text-xs font-semibold leading-4 text-[#334155]">
      {children}
      {required && <span className="font-semibold text-[#ef4444]"> *</span>}
    </label>
  );
}

/** Active / Inactive segmented pill. */
function StatusToggle({ active, onChange }: { active: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-[#64748b]">Status:</span>
      <div className="flex items-center gap-[1px] rounded-lg border border-brand-shade bg-brand-shade p-[3px]">
        {[
          { label: 'Active', value: true },
          { label: 'Inactive', value: false },
        ].map(({ label, value }) => (
          <button
            key={label}
            type="button"
            onClick={() => onChange(value)}
            className={[
              'rounded-md px-3 py-1 text-xs transition-colors',
              active === value
                ? 'bg-white font-semibold text-brand shadow-card'
                : 'font-medium text-[#46556f] hover:text-brand',
            ].join(' ')}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

/** One designation row in the hierarchy table. */
function DesignationRow({
  row,
  escalatesTo,
  onDelete,
}: {
  row: Designation;
  /** Derived senior-post label ("<name> (Rank N)"), or null for the apex post. */
  escalatesTo: string | null;
  onDelete: () => void;
}) {
  return (
    <div className={[GRID, 'border-t border-line-soft', row.terminal ? 'bg-brand-shade/20' : 'bg-white'].join(' ')}>
      {/* Rank — drag handle + badge */}
      <div className="flex items-center gap-1.5 px-4 py-3">
        <DragHandleIcon className="size-3.5 shrink-0 text-[#94a3b8]" />
        <span className="flex size-6 items-center justify-center rounded-md border border-[#d1e2ff] bg-brand-shade text-xs font-bold text-brand">
          {row.rank}
        </span>
      </div>

      {/* Designation name */}
      <div className="px-4 py-3 pl-8">
        <p className="text-xs font-semibold leading-4 text-ink">{row.name}</p>
      </div>

      {/* Territory level pill */}
      <div className="px-4 py-3">
        <span
          className={[
            'inline-flex items-center rounded-full border px-[11px] py-[3px] text-xs font-medium',
            LEVEL_TONES[row.tone],
          ].join(' ')}
        >
          {row.level}
        </span>
      </div>

      {/* Lowest level? */}
      <div className="flex justify-center px-4 py-3">
        {row.terminal ? (
          <span className="rounded bg-[#d1fae5] px-2 py-0.5 text-center text-[11px] font-semibold text-[#065f46]">
            Yes - Terminal Post
          </span>
        ) : (
          <span className="text-xs font-medium text-[#94a3b8]">No</span>
        )}
      </div>

      {/* Escalates to */}
      <div className="px-4 py-3">
        {escalatesTo ? (
          <span className="flex items-center gap-1">
            <EscalateArrowIcon className="shrink-0 text-brand" />
            <span className="text-xs text-ink">{escalatesTo}</span>
          </span>
        ) : (
          <span className="text-xs italic text-[#64748b]">— (Apex Post)</span>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end px-4 py-3">
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Remove ${row.name}`}
          className="rounded p-1 text-[#94a3b8] transition-colors hover:bg-red-50 hover:text-red-500"
        >
          <TrashIcon className="size-4" />
        </button>
      </div>
    </div>
  );
}

export default function CreateGovernmentDepartmentPage() {
  const navigate = useNavigate();
  const [active, setActive] = useState(true);
  const [name, setName] = useState('Revenue & Land Reforms Department');
  const [territory, setTerritory] = useState<string>('State Revenue (General Administration)');
  const [controlMode, setControlMode] = useState<ControlMode>('dual');
  const [controlOpen, setControlOpen] = useState(true);
  const [description, setDescription] = useState(
    'Primary nodal department responsible for land records management, revenue assessment, district level land dispute monitoring, and emergency disaster assistance co-ordination across all state districts.',
  );
  const [designations, setDesignations] = useState<Designation[]>(SEED_DESIGNATIONS);
  const [modalOpen, setModalOpen] = useState(false);

  // Display order is driven by rank (ascending); escalation always points one rank up.
  const sorted = [...designations].sort((a, b) => a.rank - b.rank);
  const nextRank = sorted.length ? sorted[sorted.length - 1].rank + 1 : 1;

  const addDesignation = (d: NewDesignation) => {
    setDesignations((rows) => [...rows, { ...d, id: `d${Date.now()}` }]);
    setModalOpen(false);
  };

  const removeDesignation = (id: string) =>
    setDesignations((rows) => rows.filter((r) => r.id !== id));

  const handleSave = () => {
    if (!name.trim()) {
      message.error('Department name is required.');
      return;
    }
    message.success('Department saved.');
    navigate('/admin/dept-configuration');
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div className="flex flex-col gap-1">
        <Breadcrumbs
          items={[
            { label: 'Government Dep.', to: '/admin/dept-configuration' },
            { label: 'Create New' },
          ]}
        />
        <h1 className="pt-1 text-2xl font-bold leading-8 tracking-[-0.6px] text-[#0f172a]">
          Create Government Department
        </h1>
        <p className="text-sm leading-5 text-[#64748b]">
          Register a new government department and configure its internal hierarchy and designations.
        </p>
      </div>

      {/* Section: Department Identity */}
      <section className="flex flex-col gap-6 rounded-xl border border-[rgba(226,232,240,0.9)] bg-white p-[29px] shadow-card">
        <header className="flex items-start justify-between border-b border-[#f1f5f9] pb-5">
          <div className="flex flex-col gap-1.5">
            <h2 className="text-base font-bold leading-4 text-[#0f172a]">Department Identity</h2>
            <p className="text-xs leading-4 text-[#64748b]">
              Specify core identifiers, territory hierarchy, and operational status.
            </p>
          </div>
          <StatusToggle active={active} onChange={setActive} />
        </header>

        <div className="flex flex-col gap-5">
          <div className="flex flex-col gap-5 md:flex-row">
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <FieldLabel required>Department Name</FieldLabel>
              <input
                className={INPUT}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Revenue & Land Reforms Department"
              />
            </div>
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <FieldLabel>Territory Hierarchy</FieldLabel>
              <Select
                className="w-full"
                size="large"
                value={territory}
                onChange={setTerritory}
                options={TERRITORY_HIERARCHIES.map((t) => ({ value: t, label: t }))}
              />
            </div>
          </div>

          {/* Control Mode — expandable selector */}
          <div className="flex flex-col gap-1.5">
            <button
              type="button"
              onClick={() => setControlOpen((o) => !o)}
              className="flex items-center justify-between"
              aria-expanded={controlOpen}
            >
              <span className="text-xs font-semibold leading-4 text-[#334155]">
                Control Mode <span className="text-[#ef4444]">*</span>
              </span>
              <CaretIcon
                className={['size-5 text-[#334155] transition-transform', controlOpen ? 'rotate-180' : ''].join(' ')}
              />
            </button>
            {controlOpen && (
              <div className="overflow-hidden rounded-lg border border-line-soft shadow-card">
                {CONTROL_MODES.map((m, i) => {
                  const selected = controlMode === m.key;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => setControlMode(m.key)}
                      className={[
                        'flex w-full items-start justify-between gap-3 p-3 text-left transition-colors',
                        i > 0 ? 'border-t border-[#f1f5f9]' : '',
                        selected ? 'bg-brand-shade/60' : 'bg-white hover:bg-[#f8fafc]',
                      ].join(' ')}
                    >
                      <div className="flex flex-col gap-px">
                        <span
                          className={[
                            'text-xs',
                            selected ? 'font-semibold text-[#1e3a8a]' : 'font-medium text-[#0f172a]',
                          ].join(' ')}
                        >
                          {m.title}
                        </span>
                        <span
                          className={[
                            'text-[11px] leading-[17.88px]',
                            selected ? 'text-[#475569]' : 'text-[#64748b]',
                          ].join(' ')}
                        >
                          {m.desc}
                        </span>
                      </div>
                      {selected && <CheckmarkIcon className="mt-0.5 size-4 shrink-0 text-brand" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1.5 pb-1.5">
            <FieldLabel>Description</FieldLabel>
            <textarea
              rows={3}
              className={[INPUT, 'resize-none leading-5'].join(' ')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* Section: Designations & Rank */}
      <section className="flex flex-col gap-6 rounded-xl border border-[rgba(226,232,240,0.9)] bg-white p-[29px] shadow-card">
        <header className="flex items-center justify-between border-b border-[#f1f5f9] pb-5">
          <div className="flex flex-col gap-1.5">
            <h2 className="text-base font-bold leading-4 text-ink">Designations &amp; Rank</h2>
            <p className="text-xs leading-4 text-[#64748b]">
              Add designations, assign numeric ranks (1 = highest seniority), territory levels, and set
              escalation flow.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="flex shrink-0 items-center gap-1.5 rounded-lg border border-[#d1e2ff] bg-brand-shade px-[13px] py-[7px] text-xs font-semibold text-brand transition-colors hover:bg-[#e0ebff]"
          >
           + Add Designation
          </button>
        </header>

        {/* Hierarchy table */}
        <div className="overflow-x-auto rounded-lg border border-line-soft">
          <div className="min-w-[840px]">
            {/* Header row */}
            <div className={[GRID, 'bg-[#f8fafc]'].join(' ')}>
              {(
                [
                  ['RANK', 'justify-start'],
                  ['DESIGNATION NAME', 'justify-start pl-8'],
                  ['TERRITORY LEVEL', 'justify-start'],
                  ['LOWEST LEVEL?', 'justify-center text-center'],
                  ['ESCALATES TO', 'justify-start'],
                  ['ACTIONS', 'justify-end text-right'],
                ] as const
              ).map(([label, align]) => (
                <div key={label} className={['flex px-4 py-3', align].join(' ')}>
                  <span className="text-xs font-bold uppercase tracking-[0.6px] text-ink">{label}</span>
                </div>
              ))}
            </div>

            {/* Body rows — escalation points to the next-senior post (one rank up) */}
            {sorted.map((row, i) => {
              const senior = i > 0 ? sorted[i - 1] : null;
              return (
                <DesignationRow
                  key={row.id}
                  row={row}
                  escalatesTo={senior ? `${baseName(senior.name)} (Rank ${senior.rank})` : null}
                  onDelete={() => removeDesignation(row.id)}
                />
              );
            })}
          </div>
        </div>

        {/* Helper callout */}
        <div className="flex items-start gap-2.5 rounded-lg border border-[#dbeafe] bg-[rgba(239,246,255,0.7)] p-[15px]">
          <InfoCircleIcon className="mt-0.5 size-4 shrink-0 text-brand" />
          <p className="text-xs leading-[19.5px] text-[#475569]">
            <span className="font-semibold text-ink">Rank Rule Note:</span> Smaller rank numbers
            indicate higher seniority (Rank 1 is highest). Escalation automatically moves from the
            assigned officer to the next officer of a smaller rank number.
          </p>
        </div>
      </section>

      {/* Action footer */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="rounded-lg border border-[#cbd5e1] bg-white px-[25px] py-[11px] text-sm font-medium text-[#334155] transition-colors hover:bg-brand-shade"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="rounded-lg bg-brand px-6 py-2.5 text-sm font-semibold text-white shadow-card transition-colors hover:bg-brand-dark"
        >
          Save &amp; Proceed
        </button>
      </div>

      <AddDesignationModal
        open={modalOpen}
        defaultRank={nextRank}
        onCancel={() => setModalOpen(false)}
        onAdd={addDesignation}
      />
    </div>
  );
}
